import initSqlJs, { type Database, type Statement } from 'sql.js';
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

/**
 * Web stand-in for expo-sqlite, backed by sql.js (SQLite compiled to wasm).
 *
 * Same engine and same dialect as the native build, so `db/migrations/*.sql`
 * replay verbatim and the Drizzle schema needs no web variant.
 *
 * Two things sql.js does not give us and this module adds:
 *
 *  - **Durability.** sql.js is in-memory. The whole database is exported and
 *    written to IndexedDB shortly after each write, and on page hide.
 *  - **Change notification.** `useLiveQuery` on native leans on expo-sqlite's
 *    change listener. Statements are inspected as they are prepared, and any
 *    mutating one notifies subscribers once it has run.
 *
 * `openDatabaseSync` is synchronous because `db/client.ts` calls it at module
 * scope. `initSqlite()` must therefore be awaited by the entry point *before*
 * anything imports the database — see `web/main.tsx`.
 */

const IDB_NAME = 'aac-sqlite';
const IDB_STORE = 'db';
const IDB_KEY = 'aac.db';
const PERSIST_DEBOUNCE_MS = 250;
/** Past this, assume storage is unreachable and start without it. */
const OPEN_TIMEOUT_MS = 5000;

let database: Database | null = null;
let idb: IDBDatabase | null = null;

const listeners = new Set<() => void>();
let persistTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Raced against a deadline, for the same reason as the file store: an
 * `indexedDB.open()` that never fires an event would stall startup forever,
 * and the app would never paint.
 *
 * Null means the database runs in memory for this session. `persistNow`
 * already no-ops without a handle, so nothing is written back — which also
 * protects whatever is on disk from being overwritten by a fresh empty
 * database that only looks authoritative.
 */
function openIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) return;
      settled = true;
      resolve(db);
    };

    const timer = setTimeout(() => {
      console.warn('[sqlite] IndexedDB did not open within %dms; running in memory', OPEN_TIMEOUT_MS);
      finish(null);
    }, OPEN_TIMEOUT_MS);

    try {
      const request = indexedDB.open(IDB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(IDB_STORE)) {
          request.result.createObjectStore(IDB_STORE);
        }
      };
      request.onsuccess = () => {
        clearTimeout(timer);
        finish(request.result);
      };
      request.onerror = () => {
        clearTimeout(timer);
        finish(null);
      };
      request.onblocked = () => {
        clearTimeout(timer);
        finish(null);
      };
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

function loadSaved(): Promise<Uint8Array | null> {
  if (!idb) return Promise.resolve(null);
  return new Promise((resolve) => {
    // Reading the saved database can stall even once the handle is open.
    const timer = setTimeout(() => {
      console.warn('[sqlite] reading the saved database timed out; starting empty');
      resolve(null);
    }, OPEN_TIMEOUT_MS);
    const done = (value: Uint8Array | null) => {
      clearTimeout(timer);
      resolve(value);
    };
    try {
      const request = idb!.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(IDB_KEY);
      request.onsuccess = () => done((request.result as Uint8Array) ?? null);
      request.onerror = () => done(null);
    } catch {
      done(null);
    }
  });
}

/** Write the whole database out. Small enough that this stays cheap. */
export function persistNow(): void {
  if (!database || !idb) return;
  try {
    const bytes = database.export();
    idb.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(bytes, IDB_KEY);
  } catch (e) {
    console.warn('[sqlite] persist failed', e);
  }
}

function schedulePersist(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(persistNow, PERSIST_DEBOUNCE_MS);
}

function notify(): void {
  schedulePersist();
  for (const listener of listeners) listener();
}

/** False when this session has no durable storage; the UI says so. */
export function storageAvailable(): boolean {
  return idb !== null;
}

/** Subscribe to writes. Returns an unsubscribe function. */
export function subscribeToChanges(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const MUTATING = /^\s*(?:INSERT|UPDATE|DELETE|REPLACE|CREATE|DROP|ALTER|PRAGMA)\b/i;

/**
 * Wrap a prepared statement so a write announces itself once it has executed.
 * Drizzle drives selects with `step`/`getAsObject` and writes with `run`, but
 * `step` also executes a statement, so both paths are covered.
 */
function wrapStatement(stmt: Statement, mutating: boolean): Statement {
  if (!mutating) return stmt;

  const run = stmt.run.bind(stmt);
  const step = stmt.step.bind(stmt);

  stmt.run = (params?: never) => {
    const result = run(params);
    notify();
    return result;
  };
  stmt.step = () => {
    const result = step();
    notify();
    return result;
  };

  return stmt;
}

export type SQLiteDatabase = Database & {
  /** Present so a caller can force a flush; native has no equivalent. */
  persistNow: () => void;
};

/**
 * Load the wasm engine and restore the saved database.
 *
 * Must be awaited before any module that touches `db/client` is imported.
 */
export async function initSqlite(): Promise<void> {
  if (database) return;

  const SQL = await initSqlJs({ locateFile: () => wasmUrl });

  // Private browsing blocks IndexedDB, and an installed iOS app can simply
  // never answer. Either way the app still runs; it just will not remember
  // anything after a reload. Better than refusing to start.
  idb = await openIdb();

  const saved = await loadSaved();
  const db = saved ? new SQL.Database(saved) : new SQL.Database();

  // Foreign keys are off by default in SQLite and the schema relies on them.
  db.run('PRAGMA foreign_keys = ON;');

  const prepare = db.prepare.bind(db);
  db.prepare = (sql: string, params?: never) =>
    wrapStatement(prepare(sql, params), MUTATING.test(sql));

  const run = db.run.bind(db);
  db.run = (sql: string, params?: never) => {
    const result = run(sql, params);
    if (MUTATING.test(sql)) notify();
    return result;
  };

  database = db;

  // A refresh or a closed tab must not lose the last few writes.
  window.addEventListener('pagehide', persistNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persistNow();
  });
}

/**
 * The expo-sqlite entry point. Synchronous by contract; `initSqlite()` has
 * already done the asynchronous work.
 */
export function openDatabaseSync(
  _name: string,
  _options?: { enableChangeListener?: boolean },
): SQLiteDatabase {
  if (!database) {
    throw new Error('initSqlite() must be awaited before opening the database');
  }
  return Object.assign(database, { persistNow }) as SQLiteDatabase;
}

export function openDatabase(name: string): SQLiteDatabase {
  return openDatabaseSync(name);
}

export async function deleteDatabaseAsync(_name: string): Promise<void> {
  if (idb) {
    idb.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).delete(IDB_KEY);
  }
  database?.close();
  database = null;
}
